import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchJobFieldLeadAssignments, setPrimaryFieldLead, addAdditionalFieldLead, removeFieldLeadAssignment } from '../lib/bridge';
import { LoadingState } from '../components/ui/LoadingState';
import { ErrorState } from '../components/ui/ErrorState';
import { Button } from '../components/ui/Button';
import { Card, CardHeader } from '../components/ui/Card';
import { fieldControlSmClass, fieldLabelClass } from '../components/ui/forms';

// Owner/Admin/Manager-only: set the Primary Field Lead and add/remove
// additional Field Leads for a job (docs/V6_ARCHITECTURE_AND_MIGRATION_PLAN.md
// §9.2). Primary/Additional is a responsibility/assignment label only —
// every Field Lead has the identical standard capability set regardless of
// which label applies on a given job (§9), so this panel never grants or
// restricts any permission; it only changes who a job's "Assigned Jobs"
// list shows it to.
export function FieldLeadAssignmentPanel({ jobId }: { jobId: string }) {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['jobFieldLeadAssignments', jobId], queryFn: () => fetchJobFieldLeadAssignments(jobId) });
  const [primaryUsername, setPrimaryUsername] = useState('');
  const [additionalUsername, setAdditionalUsername] = useState('');

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['jobFieldLeadAssignments', jobId] });
  }

  const setPrimary = useMutation({
    mutationFn: () => setPrimaryFieldLead(jobId, primaryUsername.trim()),
    onSuccess: (result) => {
      if (result.ok) {
        setPrimaryUsername('');
        invalidate();
      }
    },
  });

  const addAdditional = useMutation({
    mutationFn: () => addAdditionalFieldLead(jobId, additionalUsername.trim()),
    onSuccess: (result) => {
      if (result.ok) {
        setAdditionalUsername('');
        invalidate();
      }
    },
  });

  const remove = useMutation({
    mutationFn: (username: string) => removeFieldLeadAssignment(jobId, username),
    onSuccess: (result) => result.ok && invalidate(),
  });

  return (
    <Card>
      <CardHeader title="Field Lead Assignment" />

      {query.isLoading && <LoadingState label="Loading Field Lead assignment…" />}
      {query.isError && (
        <ErrorState message={query.error instanceof Error ? query.error.message : 'Could not load Field Lead assignment.'} onRetry={() => query.refetch()} />
      )}
      {query.data && !query.data.ok && <ErrorState message={query.data.message || 'Could not load Field Lead assignment.'} onRetry={() => query.refetch()} />}

      {query.data?.ok && query.data.assignments && (
        <div className="mb-4 space-y-2 text-sm">
          <p className="text-slate-300">
            Primary: <span className="font-semibold text-white">{query.data.assignments.primary || 'Not assigned'}</span>
          </p>
          {query.data.assignments.additional.length > 0 && (
            <ul className="space-y-1.5">
              {query.data.assignments.additional.map((username) => (
                <li key={username} className="flex items-center justify-between gap-2 rounded-lg border border-ink-border bg-ink px-3 py-2">
                  <span className="text-slate-300">Additional: {username}</span>
                  <button
                    type="button"
                    onClick={() => remove.mutate(username)}
                    disabled={remove.isPending}
                    className="text-xs font-semibold text-red-300 hover:text-red-200 disabled:opacity-60"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (primaryUsername.trim()) setPrimary.mutate();
        }}
      >
        <div className="space-y-1">
          <label htmlFor="fl-primary" className={fieldLabelClass}>
            Set Primary Field Lead (username)
          </label>
          <input id="fl-primary" className={fieldControlSmClass} value={primaryUsername} onChange={(e) => setPrimaryUsername(e.target.value)} />
        </div>
        <Button type="submit" variant="secondary" busy={setPrimary.isPending} busyLabel="Saving…">
          Set Primary
        </Button>
      </form>
      {setPrimary.data && !setPrimary.data.ok && <ErrorState message={String(setPrimary.data.message || 'Could not set the Primary Field Lead.')} />}

      <form
        className="mt-3 flex flex-wrap items-end gap-2"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (additionalUsername.trim()) addAdditional.mutate();
        }}
      >
        <div className="space-y-1">
          <label htmlFor="fl-additional" className={fieldLabelClass}>
            Add Additional Field Lead (username)
          </label>
          <input id="fl-additional" className={fieldControlSmClass} value={additionalUsername} onChange={(e) => setAdditionalUsername(e.target.value)} />
        </div>
        <Button type="submit" variant="secondary" busy={addAdditional.isPending} busyLabel="Adding…">
          Add Additional
        </Button>
      </form>
      {addAdditional.data && !addAdditional.data.ok && <ErrorState message={String(addAdditional.data.message || 'Could not add the additional Field Lead.')} />}
      {remove.data && !remove.data.ok && <ErrorState message={String(remove.data.message || 'Could not remove that Field Lead assignment.')} />}
    </Card>
  );
}
